import { useCallback, useContext, useState } from "react";
import {
  ContactCardCheckout,
  LoadingOverlay,
} from "@sunrun/experience-ui-components";
import type { Contact } from "@sunrun/experience-ui-components/lib/components/ContactCard";
import { Role } from "../../../amplify/backend/function/OfferExpContacts/ts/public/types";
import { useContactManagement } from "hooks/useContactManagement";
import { ROLES } from "utils/usageUtils";
import { ErrorModal } from "components/molecules/ErrorModal";
import { UsageFormContext } from "providers/UsageFormContext";
import { modals } from "fixtures/modals";

const { RESEND_NEM } = modals;

type UsageSlideoutContactProps = {
  selectedUtilityContact: Contact | undefined;
  setSelectedUtilityContact: (contact: Contact) => void;
  refSetter?: (node: HTMLDivElement | null) => void;
  hasOffers?: boolean;
  refetchContacts: () => void;
  hasSignedOffer?: boolean;
};

const UsageSlideoutContacts = ({
  selectedUtilityContact,
  setSelectedUtilityContact,
  refSetter,
  hasOffers,
  hasSignedOffer,
}: UsageSlideoutContactProps) => {
  const { setShowUtilityBillWarningBanner } = useContext(UsageFormContext);

  const [isLoading, setIsLoading] = useState(false);

  const {
    contactList,
    onAddContact: saveContact,
    onEditContact: editContact,
    isContactsLoading,
    error,
    setError,
  } = useContactManagement();

  const findContactById = (id: string) =>
    contactList?.find((contact) => contact.contactId === id);

  const onContactInfoSave = useCallback(
    async (contactToUpdate: Contact, role?: Role) => {
      const {
        contactId,
        customerFirstName,
        customerLastName,
        customerPrimaryPhone,
        customerEmail,
      } = contactToUpdate;

      const isEditingAnyName = !!contactList?.find((contact) => {
        return (
          contact.contactId === contactId &&
          (contact.customerFirstName !== customerFirstName ||
            contact.customerLastName !== customerLastName)
        );
      });

      if (isEditingAnyName || !contactToUpdate.contactId) {
        try {
          setIsLoading(true);
          const savedContact = await saveContact({
            customerEmail,
            customerFirstName,
            customerLastName,
            customerPrimaryPhone,
            role: role ?? ROLES.OTHER,
            ...(role === ROLES.HOMEOWNER && { primary: true }),
          });
          setIsLoading(false);
          return savedContact;
        } catch (error) {
          setError({ action: "Creating new Contact", error: error as Error });
          return contactToUpdate;
        }
      } else {
        try {
          setIsLoading(true);
          await editContact(contactToUpdate);
          setIsLoading(false);
          return contactToUpdate;
        } catch (error) {
          setError({ action: "Editing Contact", error: error as Error });
        }
      }
    },
    [contactList, saveContact, editContact, setError]
  );

  const handleBillingSelectionUpdate = async (selectedContactId: string) => {
    const selectedContact = findContactById(selectedContactId);

    if (selectedContact) {
      setSelectedUtilityContact(selectedContact);
    }
    if (selectedContactId !== selectedUtilityContact?.contactId) {
      setShowUtilityBillWarningBanner(true);
    }
  };

  return (
    <>
      <div ref={refSetter}>
        <ContactCardCheckout
          disableEditEmail={hasSignedOffer}
          disableEditPhone={hasSignedOffer}
          selectedContactId={selectedUtilityContact?.contactId}
          toolTipText="The utility bill contact must exactly match the name/spelling on the household's utility bills. This contact will be the signer for the NEM agreements and apply for utility approval."
          contactList={contactList || []}
          contactType="utility_bill"
          cardTitleText="Utility Bill Contact (Optional)"
          showCreditStatus={false}
          onContactInfoSave={onContactInfoSave}
          onSelectionUpdate={handleBillingSelectionUpdate}
          modals={[RESEND_NEM]}
          hasOffer={hasOffers}
        />
      </div>
      {!!error && (
        <ErrorModal
          context="UsageSlideoutContacts"
          error={error.error}
          action={error.action}
          onClose={() => setError(undefined)}
        />
      )}
      {(!!isContactsLoading || !!isLoading) && (
        <LoadingOverlay
          message="Processing..."
          data-testid="processing-overlay"
        />
      )}
    </>
  );
};

export { UsageSlideoutContacts };
export type { UsageSlideoutContactProps };
